import { useEffect, useState } from "react";
import api, { getToken } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X, Save, Upload, Loader2, Image as ImageIcon, ExternalLink } from "lucide-react";

const imgSrc = (url) => (url && url.includes("/api/files/")) ? `${url}${url.includes("?") ? "&" : "?"}auth=${getToken()}` : url;

export const REAL_TYPES = {
  bache: "Bâcherie", sellerie_auto: "Sellerie auto", sellerie_marine: "Sellerie marine",
  coussin: "Coussins", capote: "Capotes / Tauds", cuir: "Cuir", habitat: "Habitat / Déco", autre: "Autres",
};

const emptyForm = () => ({ title: "", type: "sellerie_auto", description: "", image: "" });

export default function Realisations() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/realisations").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyForm()); setEditId(null); setShowForm(true); };
  const openEdit = (it) => { setForm({ title: it.title, type: it.type, description: it.description || "", image: it.image || "" }); setEditId(it.id); setShowForm(true); };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: data.url }));
      toast.success("Photo téléversée");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'upload"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Le titre est obligatoire");
    setSaving(true);
    try {
      if (editId) { await api.put(`/realisations/${editId}`, form); toast.success("Réalisation mise à jour"); }
      else { await api.post("/realisations", form); toast.success("Réalisation ajoutée"); }
      setShowForm(false); load();
    } catch { toast.error("Erreur lors de l'enregistrement"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette réalisation ?")) return;
    await api.delete(`/realisations/${id}`); load(); toast.success("Réalisation supprimée");
  };

  return (
    <div className="space-y-5 animate-fade-up" data-testid="realisations-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-display font-bold text-navy">Réalisations</h1>
          <p className="text-sm text-slate-500 mt-0.5">Votre portfolio public, présenté par type de réalisation sur votre fiche annuaire.</p>
        </div>
        <button onClick={openNew} data-testid="realisation-add-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-navy text-white text-sm font-semibold hover:bg-navy-deep transition-all active:scale-95">
          <Plus className="w-4 h-4" /> Nouvelle réalisation
        </button>
      </div>

      {items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-12 text-center" data-testid="realisations-empty">
          <ImageIcon className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Ajoutez vos plus belles réalisations pour valoriser votre savoir-faire.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it) => (
            <div key={it.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden group transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md" data-testid={`realisation-card-${it.id}`}>
              <div className="aspect-[4/3] bg-slate-100 overflow-hidden relative">
                {it.image ? <img src={imgSrc(it.image)} alt={it.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" onError={(e) => { e.target.style.display = "none"; }} />
                  : <div className="w-full h-full flex items-center justify-center"><ImageIcon className="w-8 h-8 text-slate-300" /></div>}
                <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-navy/85 text-sky-300 text-[10px] font-bold uppercase tracking-wide">{REAL_TYPES[it.type] || it.type}</span>
              </div>
              <div className="p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-navy truncate">{it.title}</div>
                    {it.description && <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{it.description}</div>}
                  </div>
                  <div className="flex shrink-0">
                    <button onClick={() => openEdit(it)} data-testid={`realisation-edit-${it.id}`} className="p-1.5 rounded-lg text-slate-400 hover:text-sky-600 hover:bg-sky-50"><Save className="w-4 h-4" /></button>
                    <button onClick={() => remove(it.id)} data-testid={`realisation-delete-${it.id}`} className="p-1.5 rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-50"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setShowForm(false)} data-testid="realisation-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-display font-bold text-navy">{editId ? "Modifier la réalisation" : "Nouvelle réalisation"}</h2>
              <button onClick={() => setShowForm(false)} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-500">Titre *</label>
                <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="realisation-title"
                  className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Type de réalisation</label>
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="realisation-type"
                  className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sky-400">
                  {Object.entries(REAL_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Photo</label>
                <div className="mt-1 flex items-center gap-3">
                  <div className="w-20 h-20 rounded-xl border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center shrink-0">
                    {form.image ? <img src={imgSrc(form.image)} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-6 h-6 text-slate-300" />}
                  </div>
                  <label className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 text-slate-600 text-sm font-medium cursor-pointer hover:border-sky-400" data-testid="realisation-upload">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Téléverser
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0])} />
                  </label>
                </div>
                <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} data-testid="realisation-image-url"
                  placeholder="ou collez une URL d'image publique (https://…)"
                  className="mt-2 w-full px-3 py-2 rounded-xl border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-sky-400" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Description (optionnel)</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} data-testid="realisation-description"
                  className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium hover:bg-slate-50">Annuler</button>
              <button onClick={save} disabled={saving} data-testid="realisation-save" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-navy text-white text-sm font-semibold hover:bg-navy-deep disabled:opacity-60">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
